import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { useLocation } from "react-router";
import styled from "styled-components";
import avatar1 from "@assets/images/variant-jaangal/auth/profiles/onboarding-avatar-1.png";
import avatar2 from "@assets/images/variant-jaangal/auth/profiles/onboarding-avatar-2.png";
import avatar4 from "@assets/images/variant-jaangal/auth/profiles/onboarding-avatar-4.png";
import avatar6 from "@assets/images/variant-jaangal/auth/profiles/onboarding-avatar-6.png";
import { useRegisterState } from "@domain/currentProfile/hooks";
import { RegisterState } from "@domain/currentProfile/registerState";
import { LoginForm } from "./loginForm";
import { SignupChannel, useSignupStepCount } from "@ui/pages/loginPage/onboarding/authStep";
import { Onboarding } from "@ui/pages/loginPage/onboarding/onboarding";
import { Signup } from "@ui/pages/loginPage/onboarding/signup";

export type AuthLocationState = {
	initialMode?: "login" | "signup";
};

type AuthPageProps = React.HTMLAttributes<HTMLDivElement>;

export const AuthPage: React.FC<AuthPageProps> = ({ ...htmlProps }) => {
	const location = useLocation();
	const registerState = useRegisterState();
	const [loginMode, setLoginMode] = useState(location.state?.initialMode !== "signup");
	const [signupStep, setSignupStep] = useState(0);
	const [signupChannel, setSignupChannel] = useState<SignupChannel>("phone");
	const signupStepCount = useSignupStepCount(signupChannel);

	const onboarding = ![RegisterState.LOGOUT, RegisterState.LOGGED].includes(registerState);
	const showSignupSteps = !onboarding && !loginMode;

	return (
		<Shell {...htmlProps}>
			<Main>
				{showSignupSteps && (
					<SignupSteps>
						{signupStepCount > 1 && signupStep > 0 ? (
							<BackButton
								type="button"
								aria-label={I18n.t("auth.signup.back")}
								onClick={() => setSignupStep((current) => current - 1)}
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							</BackButton>
						) : (
							<span />
						)}
						{signupStepCount > 1 && (
							<Dots>
								{Array.from({ length: signupStepCount }, (_, index) => (
									<Dot key={index} $active={index === Math.min(signupStep, signupStepCount - 1)} />
								))}
							</Dots>
						)}
					</SignupSteps>
				)}
				<Box>
					{onboarding ? (
						<Onboarding />
					) : loginMode ? (
						<LoginForm onSignup={() => setLoginMode(false)} />
					) : (
						<Signup
							step={signupStep}
							channel={signupChannel}
							onStepChange={setSignupStep}
							onChannelChange={setSignupChannel}
							onLogin={() => setLoginMode(true)}
						/>
					)}
				</Box>
			</Main>
			<Brand>
				<Wordmark>
					Jaangal <em>By Tooshare</em>
				</Wordmark>
				<Hero>
					<HeroTitle>{I18n.t("auth.hero.title")}</HeroTitle>
					<HeroLead>{I18n.t("auth.hero.lead")}</HeroLead>
					<Cluster>
						<img src={avatar1} alt="" />
						<img src={avatar2} alt="" />
						<img src={avatar4} alt="" />
						<img src={avatar6} alt="" />
						<More>{I18n.t("auth.hero.members")}</More>
					</Cluster>
				</Hero>
				<Powered>{I18n.t("auth.hero.powered")}</Powered>
			</Brand>
		</Shell>
	);
};

const Shell = styled.div`
	width: 100%;
	flex: 1;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	background: ${({ theme }) => theme.colors.white};

	@media (min-width: 900px) {
		display: grid;
		grid-template-columns: 1fr 1fr;
		height: 100vh;
		overflow: hidden;
	}
`;

const Main = styled.main`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 100vh;
	padding: 20px 20px 56px;
	background: #ffffff;

	@media (min-width: 900px) {
		height: 100vh;
		padding: 20px 40px 40px;
		overflow-y: auto;
	}
`;

const SignupSteps = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	min-height: 44px;
	margin-bottom: 18px;
	flex-shrink: 0;
`;

const BackButton = styled.button`
	position: relative;
	z-index: 1;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: #f1f3f6;
	color: #5b6272;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	&:hover {
		background: #e7f4ec;
		color: #00863d;
	}
`;

const Dots = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	display: flex;
	justify-content: center;
	gap: 7px;
	pointer-events: none;
`;

const Dot = styled.span<{ $active: boolean }>`
	width: ${({ $active }) => ($active ? "22px" : "8px")};
	height: 8px;
	border-radius: ${({ $active }) => ($active ? "4px" : "50%")};
	background: ${({ $active }) => ($active ? "#00863d" : "#e3e7ec")};
`;

const Box = styled.div`
	width: 100%;
	max-width: 440px;
	margin: auto;
`;

const Brand = styled.aside`
	display: none;

	@media (min-width: 900px) {
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100vh;
		padding: 48px 56px;
		color: ${({ theme }) => theme.colors.white};
		background: linear-gradient(120deg, #007a37 0%, #00a24a 52%, #37c96f 100%);

		&::before,
		&::after {
			content: "";
			position: absolute;
			border-radius: 50%;
			background: rgba(255, 255, 255, 0.08);
		}

		&::before {
			width: 420px;
			height: 420px;
			top: -160px;
			right: -120px;
		}

		&::after {
			width: 280px;
			height: 280px;
			bottom: -120px;
			left: -80px;
			background: rgba(255, 255, 255, 0.06);
		}

		> * {
			position: relative;
			z-index: 1;
		}
	}
`;

const Wordmark = styled.div`
	font-size: 27px;
	font-weight: 700;
	letter-spacing: -0.5px;
	color: #ffffff;

	em {
		font-style: normal;
		font-size: 12px;
		font-weight: 400;
		color: rgba(255, 255, 255, 0.75);
		margin-left: 7px;
	}
`;

const Hero = styled.div`
	max-width: 440px;
`;

const HeroTitle = styled.h1`
	margin: 0 0 12px;
	font-size: 38px;
	line-height: 1.14;
	font-weight: 700;
`;

const HeroLead = styled.p`
	margin: 0 0 20px;
	font-size: 16px;
	line-height: 1.5;
`;

const Cluster = styled.div`
	display: flex;
	align-items: center;
	margin-top: 32px;

	> * {
		width: 52px;
		height: 52px;
		border-radius: 50%;
		object-fit: cover;
		margin-left: -14px;
	}

	> :first-child {
		margin-left: 0;
	}

	> :not(:last-child) {
		mask: radial-gradient(circle at 64px 50%, transparent 0 29px, #000 30px);
	}
`;

const More = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.22);
	font-weight: 700;
	font-size: 14px;
`;

const Powered = styled.div`
	font-size: 14px;
	opacity: 0.85;
`;
